import React, { useState, useEffect } from "react";
import Container from "@/components/marketing/Container";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/lib/AuthContext";
import { getPostLoginPath } from "@/lib/permissions";

const NAV_LINKS = [
  {
    label: "Services",
    path: "/services",
    children: [
      { label: "Journalist Outreach", path: "/services/journalist-outreach" },
      { label: "Newswire Distribution", path: "/services/newswire-distribution" },
      { label: "Guaranteed Placements", path: "/services/guaranteed-placements" },
      { label: "Find Your Fit", path: "/services/find-your-fit" },
      { label: "Reputation Management", path: "/services/reputation-management" },
    ],
  },
  {
    label: "Packages",
    path: "/packages",
    children: [
      { label: "MENA", path: "/mena" },
      { label: "UAE", path: "/uae" },
      { label: "Saudi Arabia", path: "/saudi" },
      { label: "UK", path: "/uk" },
      { label: "USA", path: "/usa" },
      { label: "Fintech", path: "/fintech" },
      { label: "Startups", path: "/startup" },
      { label: "Crypto", path: "/crypto" },
    ],
  },
  { label: "Media Network", path: "/media-network" },
  { label: "Resellers", path: "/resellers" },
  { label: "Newsroom", path: "/newsroom" },
  {
    label: "Resources",
    path: "/resources",
    children: [
      { label: "How It Works", path: "/how-it-works" },
      { label: "Guides", path: "/guides" },
      { label: "Case Studies", path: "/case-studies" },
      { label: "Editorial Calendar", path: "/editorial-calendar" },
      { label: "Help Centre", path: "/help" },
    ],
  },
  { label: "About", path: "/about" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();

  const getStartedTo = isAuthenticated ? "/dashboard/new-order" : "/register";
  const dashboardPath = isAuthenticated ? getPostLoginPath(user?.role) : "/login";
  const dashboardLabel = user?.role === "admin" || user?.role === "super_admin" ? "Admin Panel" : "Dashboard";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setOpenDropdown(null); }, [location]);

  return (
    <nav className="glass-nav fixed top-0 left-0 right-0 z-50">
      <Container className="max-w-[1400px]">
        <div className="flex items-center h-[52px]">
          <div className="flex-1 flex items-center justify-start">
            {/* Logo */}
            <Logo />
          </div>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-7 text-[13px] text-[#6c6c74] whitespace-nowrap">
            {NAV_LINKS.map((item) =>
              item.children ? (
                <div key={item.label} className="relative"
                  onMouseEnter={() => setOpenDropdown(item.label)}
                  onMouseLeave={() => setOpenDropdown(null)}>
                  <Link to={item.path} className="flex items-center gap-1 py-2 hover:text-[#1c1c20] transition-colors font-medium">
                    {item.label} <ChevronDown className="w-3 h-3" />
                  </Link>
                  {openDropdown === item.label && (
                    <div className="absolute top-full left-0 pt-1 w-56 animate-fade">
                      <div className="glass-card rounded-2xl py-2 shadow-xl" style={{ borderRadius: 16, background: "rgba(255,255,255,0.94)" }}>
                        {item.children.map((child) => (
                          <Link key={child.path} to={child.path}
                            className="block px-4 py-2.5 text-[13px] text-[#6c6c74] hover:text-[#1c1c20] hover:bg-[rgba(139,92,246,0.06)] transition-colors">
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link key={item.path} to={item.path}
                  className={`py-2 font-medium transition-colors whitespace-nowrap ${location.pathname === item.path ? "text-[#1c1c20] font-semibold" : "text-[#6c6c74] hover:text-[#1c1c20]"}`}>
                  {item.label}
                </Link>
              )
            )}
          </div>

          <div className="flex-1 flex items-center justify-end gap-4">
            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-3">
              {isAuthenticated ? (
                <Link to={dashboardPath} rel="nofollow" className="text-[13px] text-[#6c6c74] hover:text-[#1c1c20] font-medium whitespace-nowrap">
                  {dashboardLabel}
                </Link>
              ) : (
                <Link to="/login" rel="nofollow" className="text-[13px] text-[#6c6c74] hover:text-[#1c1c20] font-medium whitespace-nowrap">
                  Sign In
                </Link>
              )}
              <Link to={getStartedTo} rel="nofollow" data-track-label="get_started" data-track-section="nav"
                className="bg-dark-pill text-white text-[13px] font-semibold px-4 py-[7px] rounded-full whitespace-nowrap transition-colors hover:bg-[#3c3c42]">
                Get Started
              </Link>
            </div>

            {/* Mobile toggle */}
            <button
              className="lg:hidden text-[#1c1c20]"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </Container>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden glass-nav border-t border-[rgba(28,28,32,0.07)] overflow-hidden animate-fade">
          <div className="px-4 py-4 space-y-1">
            {NAV_LINKS.map((item) =>
              item.children ? (
                <div key={item.label}>
                  <button onClick={() => setOpenDropdown(openDropdown === item.label ? null : item.label)}
                    className="flex items-center justify-between w-full px-3 py-3 text-sm text-[#6c6c74]">
                    {item.label} <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === item.label ? "rotate-180" : ""}`} />
                  </button>
                  {openDropdown === item.label && (
                    <div className="pl-4 space-y-1">
                      {item.children.map((child) => (
                        <Link key={child.path} to={child.path}
                          className="block px-3 py-2 text-sm text-[#6c6c74] hover:text-[#1c1c20]">
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link key={item.path} to={item.path}
                  className="block px-3 py-3 text-sm text-[#6c6c74] hover:text-[#1c1c20]">
                  {item.label}
                </Link>
              )
            )}
            <div className="pt-4 flex flex-col gap-2 border-t border-[rgba(28,28,32,0.07)] mt-2">
              {isAuthenticated ? (
                <Link to={dashboardPath} rel="nofollow" className="px-3 py-2 text-sm text-[#6c6c74]">{dashboardLabel}</Link>
              ) : (
                <Link to="/login" rel="nofollow" className="px-3 py-2 text-sm text-[#6c6c74]">Sign In</Link>
              )}
              <Link to={getStartedTo} rel="nofollow" data-track-label="get_started" data-track-section="nav"
                className="bg-dark-pill text-white text-center text-sm font-semibold px-4 py-2 rounded-full whitespace-nowrap">
                Get Started
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}